// src/components/FeedbackWidget.jsx
// Bouton flottant + modal de feedback. Insère dans la table Supabase 'feedback'.

import React, { useState } from "react";
import { supabase } from "../supabase";

const TYPES = [
  { value: "bug", label: "🐛 Bug", color: "#ef4444" },
  { value: "suggestion", label: "💡 Suggestion", color: "#fbbf24" },
  { value: "question", label: "❓ Question", color: "#60a5fa" },
  { value: "autre", label: "💬 Autre", color: "#9ca3af" },
];

export function FeedbackWidget({ user, currentView, isMobile = false, hidden = false }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setType("bug");
    setMessage("");
    setSent(false);
    setError(null);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const submit = async () => {
    if (!message.trim()) return;
    setSending(true);
    setError(null);
    try {
      const { error: insertError } = await supabase.from("feedback").insert({
        user_id: user?.id || null,
        type,
        message: message.trim(),
        context: {
          view: currentView || null,
          url: window.location.href,
          userAgent: navigator.userAgent,
          screen: `${window.innerWidth}x${window.innerHeight}`,
          email: user?.email || null,
        },
      });
      if (insertError) throw insertError;
      setSent(true);
    } catch (e) {
      console.error("[FeedbackWidget] insert error:", e);
      setError("Impossible d'envoyer le feedback. Réessaie dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Bouton flottant : pastille compacte, masquée si une modale est ouverte */}
      {!hidden && (
        <button
          onClick={() => setOpen(true)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            position: "fixed",
            right: 16,
            bottom: isMobile ? 84 : 20, // au-dessus de la bottom nav mobile
            zIndex: 900, // sous les modales, la carte comparateur et la mini-fiche carte
            width: 40,
            height: 40,
            padding: 0,
            borderRadius: "50%",
            border: "1px solid rgba(167,139,250,0.4)",
            background: "rgba(167,139,250,0.95)",
            color: "#0f0f13",
            fontSize: 17,
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: hover ? 1 : 0.75,
            transition: "opacity 0.15s",
          }}
          title="Donner mon avis / signaler un problème"
          aria-label="Donner mon avis / signaler un problème"
        >
          💬
        </button>
      )}

      {/* Modal */}
      {open && (
        <div onClick={close} style={overlay}>
          <div onClick={(e) => e.stopPropagation()} style={modal}>
            <div style={header}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#f1f5f9" }}>💬 Ton retour</h2>
              <button onClick={close} style={closeBtn} aria-label="Fermer">✕</button>
            </div>

            {sent ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🙏</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 6 }}>Merci pour ton retour !</div>
                <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 18 }}>Il a bien été enregistré.</div>
                <button onClick={close} style={btnPrimary}>Fermer</button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <span style={fieldLabel}>Type</span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {TYPES.map((t) => {
                      const isSel = type === t.value;
                      return (
                        <button
                          key={t.value}
                          onClick={() => setType(t.value)}
                          style={{
                            padding: "7px 12px", borderRadius: 8,
                            border: `1px solid ${isSel ? t.color : "rgba(255,255,255,0.15)"}`,
                            background: isSel ? t.color + "22" : "transparent",
                            color: isSel ? t.color : "#9ca3af",
                            fontWeight: 600, fontSize: 12, cursor: "pointer",
                          }}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <span style={fieldLabel}>Message</span>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      type === "bug"
                        ? "Décris le problème : qu'est-ce que tu faisais, qu'est-ce qui s'est passé ?"
                        : "Dis-moi tout…"
                    }
                    style={textarea}
                    rows={5}
                    autoFocus
                  />
                </div>

                {error && (
                  <div style={{ marginBottom: 12, padding: "9px 11px", borderRadius: 8, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5", fontSize: 12 }}>
                    ⚠ {error}
                  </div>
                )}

                <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 14 }}>
                  Pour t'aider à debugger, on joint automatiquement l'écran actuel et des infos techniques (navigateur, taille d'écran). Aucune donnée sensible n'est collectée.
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={close} style={btnSecondary} disabled={sending}>Annuler</button>
                  <button onClick={submit} style={btnPrimary} disabled={sending || !message.trim()}>
                    {sending ? "Envoi…" : "Envoyer"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 2600,
  display: "flex", alignItems: "center", justifyContent: "center", padding: 14,
};
const modal = {
  background: "#0f0f13", color: "#f1f5f9", borderRadius: 18,
  border: "1px solid rgba(255,255,255,0.12)", padding: 22,
  maxWidth: 460, width: "100%", maxHeight: "90vh", overflow: "auto",
};
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 };
const closeBtn = {
  background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
  width: 30, height: 30, borderRadius: "50%", fontSize: 14, cursor: "pointer",
};
const fieldLabel = {
  display: "block", fontSize: 11, color: "#9ca3af",
  fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
};
const textarea = {
  width: "100%", marginTop: 4, background: "rgba(0,0,0,0.3)",
  border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8,
  padding: "10px 12px", fontSize: 13, color: "#fff", outline: "none",
  fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
};
const btnSecondary = {
  padding: "9px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
  background: "transparent", color: "#d1d5db", fontWeight: 600, cursor: "pointer", fontSize: 13,
};
const btnPrimary = {
  padding: "9px 16px", borderRadius: 8, border: "none",
  background: "#a78bfa", color: "#0f0f13", fontWeight: 700, cursor: "pointer", fontSize: 13, flex: 1,
};
